import { nativeApp, initialiseNativeApp } from "./services/native-app.js";
import { createApp } from "vue";
import App from "./app.vue";
import { createRouter, createWebHistory } from "vue-router";
import allRoutes from "./routes.js";
import api from "./services/api.js";
import VueGtag from "vue-gtag";
import Cookies from "js-cookie";
import "./style.scss";
import "bootstrap";
import "rst-common-vue/style.css";

import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

import { createPinia } from "pinia";
import ToastPlugin from "vue-toast-notification";
import "vue-toast-notification/dist/theme-sugar.css";
import moment from "moment";

const staleChunkReloadKey = "ordering:stale-chunk-reload";

window.addEventListener("vite:preloadError", (event) => {
	const now = Date.now();
	let shouldReload = true;

	try {
		const lastReload = Number(window.sessionStorage.getItem(staleChunkReloadKey));
		shouldReload = !lastReload || now - lastReload > 60 * 1000;

		if (shouldReload)
			window.sessionStorage.setItem(staleChunkReloadKey, now.toString());
	} catch {
		// Reload recovery should still work when session storage is unavailable.
	}

	if (!shouldReload) return;

	event.preventDefault();
	window.location.reload();
});

//import { datadogRum } from "@datadog/browser-rum";

//datadogRum.init({
//	applicationId: "0c746d83-0150-4fed-9d27-3c942d557ab0",
//	clientToken: "pubfe96396741f0bc5eec8523ed7f41c806",
//	site: "datadoghq.eu",
//	service: "ordering",
//	env: api.environment,
//	/*global __APP_VERSION__*/
//	version: __APP_VERSION__,
//	sessionSampleRate: 0,
//	sessionReplaySampleRate: 0,
//	traceSampleRate: 0,
//	telemetrySampleRate: 20,
//	trackResources: false,
//	trackLongTasks: false,
//	trackUserInteractions: false,
//	enablePrivacyForActionName: false,
//	defaultPrivacyLevel: "mask-user-input",
//	useSecureSessionCookie: true,
//});

// global functions

const router = createRouter({
	history: createWebHistory(),
	routes: allRoutes.routes,
	scrollBehavior(to, from, savedPosition) {
        if (to.meta.menuSurface && from.meta.menuSurface && to.params.companySlug === from.params.companySlug && to.params.branchSlug === from.params.branchSlug && to.params.tableSlug === from.params.tableSlug) return false;
		return new Promise((resolve, reject) => {
			if (savedPosition) {
				// create an Observer instance
				const resizeObserver = new ResizeObserver((entries) => {
					if (entries[0].target.clientHeight >= savedPosition.top + screen.height) {
						resolve(savedPosition);
						resizeObserver.disconnect();
					}
				});

				// start observing a DOM node
				resizeObserver.observe(document.body);
			} else {
				resolve({ top: 0 });
			}
		});
	},
});

router.beforeEach((to) => {
    if (nativeApp) {
        if (to.path === "/" && !nativeApp.portal) return { name: "V2Branches", params: { companySlug: nativeApp.storeSlug } };
        return;
    }
	// bounce subdomains to named routes i.e https://nonnas.iwantfed.com -> https://iwantfed.com/nonnas
	var hostParts = window.location.host.replace("www.", "").replace("co.uk", "^").split(".");
	if (hostParts.length === 3 && hostParts[0] != "www" && hostParts[0] != "order" && hostParts[0] != "table") {
		window.location.replace(`https://${hostParts[1]}.${hostParts[2].replace("^", "co.uk")}/${hostParts[0]}`);
		return;
	}
});

const pinia = createPinia();
const vueApp = createApp(App)
	.use(router)
	.use(pinia)
	.use(ToastPlugin)
	.use(VueGtag, {
		config: { id: "G-W5E4RVM9QS" },
		//enabled: false
	});

vueApp.mixin({
	methods: {
		getStorage(key) {
			try {
				const storage = nativeApp ? localStorage.getItem(`${nativeApp.tenant}:${key}`) : Cookies.get(key);
				if (!storage) return null;

				return JSON.parse(storage);
			} catch (error) {
				return null;
			}
		},
		setStorage(key, storage) {
			try {
				const cookieOptions = window.location.protocol === "https:"
					? { secure: true, sameSite: "none" }
					: { sameSite: "lax" };

				if (nativeApp) localStorage.setItem(`${nativeApp.tenant}:${key}`, JSON.stringify(storage));
                else Cookies.set(key, JSON.stringify(storage), cookieOptions);
			} catch (error) {
				return null;
			}
		},
		deleteStorage(key) {
			try {
				if (nativeApp) localStorage.removeItem(`${nativeApp.tenant}:${key}`);
                else Cookies.remove(key);
			} catch (error) {
				return null;
			}
		},
		async backToMenu(orderType, tableSlug = null) {
			if (this.branch && this.branch.slug) {
				if (!tableSlug) {
					this.$router.push({
						name: this.$route.meta.orderingV2 ? "V2MenuIntention" : "MenuIntention",
						params: { branchSlug: this.branch.slug, orderType: orderType },
					});
				} else {
					this.$router.push({
						name: this.$route.meta.orderingV2 ? "V2TableMenu" : "TableMenu",
						params: { branchSlug: this.branch.slug, orderType: orderType, tableSlug: tableSlug },
					});
				}
			}
		},
		currentBranch() {
			if (!this.store || !this.$route.params.branchSlug) return null;
			return this.store.branches.find(
				(branch) =>
					branch.slug.toString().toLowerCase() === this.$route.params.branchSlug.toString().toLowerCase(),
			);
		},

		pageErrorHandler(error) {
			if (!error) {
				this.errorMessage = "An unknown error occurred";
				return;
			}

			if (error.response?.data) {
				// error response from server
				this.errorMessage = error.response.data.message;
				return;
			}

			// local error
			this.errorMessage = error.message;
		},

		//global formatters

		formatText(text) {
			return text
				.toLowerCase()
				.replace(/\b\w/g, function (m) {
					return m.toUpperCase();
				})
				.replace("'S", "'s");
		},
		formatForSlug(value) {
			return value.toLowerCase().replace(/[\W_]+/g, "-");
		},
		formatDayNumberToName(value) {
			switch (value) {
				case 0:
					return "Sunday";
				case 1:
					return "Monday";
				case 2:
					return "Tuesday";
				case 3:
					return "Wednesday";
				case 4:
					return "Thursday";
				case 5:
					return "Friday";
				case 6:
					return "Saturday";
			}
		},
		formatTime(value) {
			if (!value) return null;
			return value.substring(0, 5);
		},
		formatDate(value) {
			return moment(value).format("DD/MM/YY");
		},
		formatDateTime(value) {
			if (!value) return null;

			var theDate = moment(value);
			if (theDate.isSame(moment(), "day")) return theDate.format("HH:mm");

			var currentDate = moment().startOf("day");
			if (theDate.diff(currentDate, "days") < 7)
				return theDate.hours() == 0 && theDate.minutes() == 0 && theDate.seconds() == 0
					? theDate.format("dddd")
					: theDate.format("dddd HH:mm");

			return theDate.hours() == 0 && theDate.minutes() == 0 && theDate.seconds() == 0
				? theDate.format("DD/MM HH:mm")
				: theDate.format("DD/MM HH:mm");
		},
		formatCurrency(currency, price) {
			var formatter = new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: currency,
			});
			return formatter.format(price);
		},
	},
});

vueApp.config.globalProperties.$http = api;

vueApp.component("DatePicker", Datepicker);

initialiseNativeApp(router);
router.isReady().then(() => vueApp.mount("#app"));
